/*!
 * Copyright © 2018-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { IEnvironmentConfig } from '../../../../environments/environment.interface';
import { EVMockDataService } from '../../../shared/services';
import { APP_CONFIG } from '../../config/app-config.token';
import {
  EVehicleSelectionMode,
  IMapHierarchyResponse,
  IRootGroupResponse,
  IVehiclePlotResponse,
  IWorkOrderPlot,
  IWorkOrderRequest,
  IWorkOrderTypesAndStatusesResponse,
  IWorkOrderUserSettings
} from '../../models';
import { IDeviceHealthFilterRequest } from '../../models/device-health.interface';
import { IDeviceHealthResponse, IDeviceHealthResponseWithCount } from '../../models/responses/device-health.response';
import { IVehiclePlotResponseForUserWithMetadata } from '../../models/vehicle-plot-with-metadata.response';
import { IDeviceHealth } from '../../store/state/device-health.state';
import { IPlotHttpService } from './plot-http.service.interface';

@Injectable()
export class PlotHttpService implements IPlotHttpService {
  private readonly _plotRootUrl: string;
  private readonly _hierarchyRootUrl: string;
  private readonly _devicesHealthRootUrl: string;

  constructor(
    private readonly _httpClient: HttpClient,
    @Inject(APP_CONFIG) private readonly _envConfig: IEnvironmentConfig,
    private readonly _evMockDataService: EVMockDataService
  ) {
    this._plotRootUrl = `${this._envConfig.endpoints.plotApiEndpoint}/Plot`;
    this._hierarchyRootUrl = `${this._envConfig.endpoints.plotApiEndpoint}/v2/Hierarchy`;
    this._devicesHealthRootUrl = `${this._envConfig.endpoints.plotApiEndpoint}/v2/DeviceHealth`;
  }

  public getVehiclePlots(entityIds: string): Observable<IVehiclePlotResponse[]> {
    const body = new HttpParams().set('SelectedEntityIds', entityIds).toString();
    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    return this._httpClient
      .post<IVehiclePlotResponse[]>(`${this._plotRootUrl}/GetVehiclePlots/`, body, { headers })
      .pipe(map(response => this._evMockDataService.mockEVBatteryData(response)));
  }

  public getAssignedWorkOrders(request: IWorkOrderRequest): Observable<IWorkOrderPlot[]> {
    return this._httpClient.post<IWorkOrderPlot[]>(`${this._plotRootUrl}/AssignedWorkOrders/`, request).pipe(map(response => response));
  }

  public getUnassignedWorkOrders(request: IWorkOrderRequest): Observable<IWorkOrderPlot[]> {
    return this._httpClient.post<IWorkOrderPlot[]>(`${this._plotRootUrl}/UnassignedWorkOrders/`, request).pipe(map(response => response));
  }

  public getWorkOrderTypesAndStatuses(): Observable<IWorkOrderTypesAndStatusesResponse> {
    return this._httpClient.get<IWorkOrderTypesAndStatusesResponse>(`${this._plotRootUrl}/WorkOrderTypesAndStatuses/`);
  }

  public getWorkOrderSettings(): Observable<IWorkOrderUserSettings> {
    return this._httpClient.get<IWorkOrderUserSettings>(`${this._plotRootUrl}/GetWorkOrderUserSettings/`);
  }

  public updateWorkOrderSettings(request: IWorkOrderUserSettings): Observable<void> {
    return this._httpClient.post<void>(`${this._plotRootUrl}/UpdateWorkOrderUserSettings/`, request);
  }

  public getRootGroupForHierarchy(): Observable<IRootGroupResponse> {
    return this._httpClient.get<IRootGroupResponse>(`${this._plotRootUrl}/GetRootGroup/`);
  }

  public getVehiclePlotsForUserWithMetadata(): Observable<IVehiclePlotResponseForUserWithMetadata> {
    return this._httpClient.get<IVehiclePlotResponseForUserWithMetadata>(`${this._plotRootUrl}/GetVehiclePlotsForUserWithMetadata`).pipe(
      map(response => {
        const mockedPlots = this._evMockDataService.mockEVBatteryData(response.Plots);
        return {
          ...response,
          Plots: mockedPlots
        };
      })
    );
  }

  public getMapHierarchy(requestMode: EVehicleSelectionMode, isScopingEnabled: boolean): Observable<IMapHierarchyResponse> {
    let params = new HttpParams();
    params = params.append('request[Mode]', requestMode.toString());
    params = params.append('request[CookieExists]', 'true');
    params = params.append('request[KeepItemsUnderRootGroup]', 'true');
    params = params.append('request[ScopedVehiclesOnly]', `${isScopingEnabled}`);

    return this._httpClient.get<IMapHierarchyResponse>(`${this._hierarchyRootUrl}/LiveMap`, { params }).pipe(map(result => result));
  }

  public getMapHierarchyScoping(): Observable<IMapHierarchyResponse> {
    return this._httpClient.get<IMapHierarchyResponse>(`${this._hierarchyRootUrl}/LiveMapScoping`).pipe(map(result => result));
  }
  public getAllDevicesHealth(request: IDeviceHealthFilterRequest, isFeatureEnabled: boolean): Observable<IDeviceHealth[]> {
    if (!isFeatureEnabled) {
      return of([]);
    }

    if (request.VehicleIds && request.VehicleIds?.length > 0) {
      return this._httpClient.post<IDeviceHealthResponse[]>(`${this._devicesHealthRootUrl}`, request).pipe(
        map(result => {
          var allDevicesHealth: IDeviceHealth[] = [];
          result.forEach(device => {
            allDevicesHealth.push(this._mapDeviceHealthStatus(device));
          });
          return allDevicesHealth;
        })
      );
    }

    return of([]);
  }

  public getCountOfAllDeviceIssuesInFleet(
    isFeatureEnabled: boolean
  ): Observable<{ devices: IDeviceHealth[]; countOfDevicesWithError: number }> {
    if (!isFeatureEnabled) {
      return of({ devices: [], countOfDevicesWithError: 0 });
    }

    return this._httpClient.get<IDeviceHealthResponseWithCount>(`${this._devicesHealthRootUrl}`).pipe(
      map(result => {
        const allDevicesHealth: IDeviceHealth[] = [];
        result.AllDeviceIssues.forEach(device => allDevicesHealth.push(this._mapDeviceHealthStatus(device)));
        return { devices: allDevicesHealth, countOfDevicesWithError: result.TotalDevicesWithIssues };
      })
    );
  }

  private _mapDeviceHealthStatus(deviceReceived: IDeviceHealthResponse): IDeviceHealth {
    return {
      hostType: deviceReceived.HostType,
      accountId: deviceReceived.AccountId,
      vehicleId: deviceReceived.HostId.toString(),
      componentHealth: deviceReceived.Components,
      cameraHealth: deviceReceived.Cameras,
      healthStatus: deviceReceived.HealthStatus
    };
  }
}
